import {useEffect, useState} from "react";
import {Area, Line} from "recharts";
import {useAtomValue} from "jotai";
import dayjs from "dayjs";
import {BaseChart} from "./BaseChart";
import {TimeSeriesDataProps} from "../../../types/timeSeriesDataProps";
import {activeFieldLoadable} from "../../../store/activeFieldStore";
import {useResourceManagement} from "../../../hooks/resourceHooks";
import {getCompanyMaderaFieldResults} from "../../../services/companyService";
import {MaderaFieldResult} from "../../../types/domain/parcel";
import {useLoadableValueOrDefault} from "../../../hooks/jotaiHooks";
import {activeDateLoadable} from "../../../store/activeDateStore";
import {fieldHistoryDaysAtom} from "../../../store/fieldHistoryDaysStore";

/**
 * ETAW Cumulative is the Madera chart
 */
export const MaderaEtawCumulativeChart: React.FC = () => {
    const fieldState = useAtomValue(activeFieldLoadable);
    const date = useLoadableValueOrDefault<dayjs.Dayjs | null>(activeDateLoadable, null);
    const fieldHistoryDays = useAtomValue(fieldHistoryDaysAtom);
    const field = fieldState.state === "hasData" && fieldState.data !== undefined ? fieldState.data : undefined;
    const resultManagement = useResourceManagement({getCompanyMaderaFieldResults});
    const [results, setResults] = useState<MaderaFieldResult>();
    const [data, setData] = useState<DataProps[]>([]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (field && date) {
            void resultManagement
                .getCompanyMaderaFieldResults(field.companyId!, field.id, date, fieldHistoryDays)
                .then((res) => setResults(res));
        }
    }, [field, date, fieldHistoryDays]);

    useEffect(() => {
        if (results) {
            setData(
                results.data?.map((d) => mapFieldResultsToChartData(d, results.allocation, results.totalAllocation)) ??
                    [],
            );
        }
    }, [results]);

    return (
        <BaseChart
            title="ETAW Cumulative"
            yLabel="AF"
            data={data}
            helpPopoverTranslationKey="help.popover.fields.etaw_cumulative_af"
        >
            <Area
                type="monotone"
                dataKey="actualEvapotranspirationAppliedWaterCumulative"
                name="ETAW Cumulative"
                unit="AF"
                yAxisId="left"
                stroke="#A1AEE3"
                fill="#E2E7F8"
                strokeWidth={2}
                isAnimationActive={false}
            />
            <Line
                type="monotone"
                dataKey="allocation"
                name="Allocation"
                unit="AF"
                yAxisId="left"
                stroke="#13AA18"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                isAnimationActive={false}
            />
            <Line
                type="monotone"
                dataKey="total"
                name="Total Allocation"
                unit="AF"
                yAxisId="left"
                stroke="#e85827"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                isAnimationActive={false}
            />
        </BaseChart>
    );
};

const mapFieldResultsToChartData = (r: MaderaFieldResult["data"][0], allocation: number, total: number): DataProps => ({
    date: dayjs(r.date).toDate(),
    actualEvapotranspirationAppliedWaterCumulative: r.value,
    allocation,
    total,
});

export interface DataProps extends TimeSeriesDataProps {
    actualEvapotranspirationAppliedWaterCumulative: number;
    allocation: number;
    total: number;
}
